<script setup lang="ts">
import type { Article } from '~/types'

interface Props {
  article: Article.Model
}

const props = defineProps<Props>()
const {
  title,
  shortDescription,
  articleDate,
  mainImage,
  permalink,
} = useArticle(props.article)
</script>

<template>
  <div class="related-item">
    <div class="related-item__inner">
      <div
        v-if="permalink && mainImage"
        class="related-item__thumb"
      >
        <NuxtLink :to="permalink">
          <UIImage
            :image="mainImage"
            class="related-item__image"
          />
        </NuxtLink>
      </div>
      <div class="related-item__meta">
        <time
          v-if="articleDate"
          class="related-item__date"
        >
          {{ articleDate }}
        </time>
        <UIDynamicHeading
          v-if="permalink && title"
          level="4"
          as="h4"
          class="related-item__title"
        >
          <NuxtLink
            :to="permalink"
            class="related-item__title-link"
          >
            {{ title }}
          </NuxtLink>
        </UIDynamicHeading>
        <p
          v-if="shortDescription"
          class="related-item__caption"
        >
          {{ shortDescription }}
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.related-item {
  &__inner {
    @apply w-full flex flex-col items-start gap-[20px];
  }

  &__thumb {
    @apply w-full h-[200px] relative rounded-[10px] overflow-hidden z-[1] bg-primary;
    @apply md:h-[220px];
    @apply xl:h-[330px];
  }

  &__image {
    @apply absolute left-0 top-0 w-full h-full object-cover -z-[1] transition-transform duration-500;
  }

  &__thumb:hover &__image {
    @apply scale-110;
  }

  &__meta {
    @apply flex flex-col gap-[10px] items-start;
  }

  &__date {
    @apply text-[14px] font-medium text-white/60 block leading-[1];
  }

  &__title {
    @apply text-white;
  }

  &__title-link {
    @apply underline underline-offset-2 decoration-transparent transition-colors duration-300;
    @apply hover:decoration-white;
  }

  &__caption {
    @apply text-[14px] text-white/60 leading-[1.3];
    @apply md:text-[16px];
  }
}
</style>
